<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Button, Dialog, Alert, Progress } from 'frappe-ui'
import {
  useProductFeed,
  getTourSteps,
  highlightTourTarget,
} from '../composables/useProductFeed'
import { session } from '../session'

const router = useRouter()
const {
  showTerms,
  announcements,
  tourDone,
  acceptTerms,
  completeTour,
  restartTour,
  latestRelease,
  tenant,
  query,
} = useProductFeed()

const termsOpen = ref(false)
const tourOpen = ref(false)
const tourIdx = ref(0)
const bannerDismissed = ref(false)
const accepting = ref(false)
const tourBusy = ref(false)

/** Force-open when user chooses Restart tour from the menu */
const forceTour = ref(false)

const termsHtml = computed(
  () =>
    query.data.value?.terms?.active?.content || '<p>No terms published.</p>',
)

const tourSteps = computed(() => getTourSteps())

watch(
  showTerms,
  (v) => {
    if (v && session.boot?.authenticated) termsOpen.value = true
  },
  { immediate: true },
)

watch(
  () =>
    !!(
      session.boot?.authenticated &&
      session.boot?.is_staff &&
      query.data.value &&
      (!tourDone.value || forceTour.value)
    ),
  (v) => {
    if (v && (!tourDone.value || forceTour.value)) {
      setTimeout(() => {
        if (!tourDone.value || forceTour.value) tourOpen.value = true
      }, 800)
    }
  },
  { immediate: true },
)

const banner = computed(() => {
  if (bannerDismissed.value) return null
  const a = announcements.value?.[0]
  if (a) {
    return {
      title: a.title,
      body: '',
      link: a.link_url || '/whats-new',
    }
  }
  if (latestRelease.value?.highlight_new) {
    return {
      title: `What's new in ${latestRelease.value.version}`,
      body: latestRelease.value.summary || latestRelease.value.title,
      link: '/whats-new',
    }
  }
  return null
})

const suspended = computed(() => {
  const fromFeed = tenant.value?.account_status
  const fromBoot = session.boot?.tenant?.account_status
  const s = fromFeed || fromBoot
  return (
    s === 'Suspended' || s === 'Deactivated' || session.boot?.tenant?.restricted
  )
})

const trialBanner = computed(() => {
  if (bannerDismissed.value) return null
  const t = tenant.value || session.boot?.tenant
  if (!t || t.account_status !== 'Trial') return null
  return {
    title: 'Trial account',
    body: t.trial_ends_on
      ? `Trial ends on ${t.trial_ends_on}.`
      : 'You are on a trial.',
  }
})

async function onAcceptTerms() {
  accepting.value = true
  try {
    await acceptTerms()
    termsOpen.value = false
  } finally {
    accepting.value = false
  }
}

async function tourNext() {
  const steps = tourSteps.value
  if (!steps.length) {
    await finishTour()
    return
  }
  if (tourIdx.value >= steps.length - 1) {
    await finishTour()
    return
  }
  tourIdx.value += 1
  const next = steps[tourIdx.value]
  if (next?.route) {
    router.push(next.route)
  }
  setTimeout(() => highlightTourTarget(next?.tourId), 300)
}

async function finishTour() {
  highlightTourTarget(null)
  tourBusy.value = true
  try {
    await completeTour()
    forceTour.value = false
    tourOpen.value = false
  } finally {
    tourBusy.value = false
  }
}

function tourSkip() {
  highlightTourTarget(null)
  completeTour()
  forceTour.value = false
  tourOpen.value = false
}

watch(tourOpen, (open) => {
  if (open) {
    tourIdx.value = 0
    const first = tourSteps.value[0]
    if (first?.route && router.currentRoute.value.path !== first.route) {
      router.push(first.route)
    }
    setTimeout(() => highlightTourTarget(first?.tourId), 250)
  } else {
    highlightTourTarget(null)
  }
})

const step = computed(() => {
  const steps = tourSteps.value
  return (
    steps[tourIdx.value] ||
    steps[0] || { title: 'Tour', body: 'No steps available for your role.' }
  )
})

/** Exposed so AppShell can start the tour from the user menu */
async function startTourFromMenu() {
  tourBusy.value = true
  try {
    await restartTour()
    forceTour.value = true
    tourIdx.value = 0
    tourOpen.value = true
  } finally {
    tourBusy.value = false
  }
}

defineExpose({ startTourFromMenu })
</script>

<template>
  <div v-if="session.boot?.authenticated" class="space-y-2">
    <Alert
      v-if="suspended"
      title="Account restricted"
      theme="red"
      variant="subtle"
    >
      This salon account is
      {{ tenant?.account_status || session.boot?.tenant?.account_status }}.
      {{
        tenant?.suspension_reason ||
        session.boot?.tenant?.suspension_reason ||
        'Contact your system administrator.'
      }}
    </Alert>

    <div
      v-if="trialBanner"
      class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-outline-amber-1 bg-surface-amber-1 px-3 py-2 text-sm text-ink-amber-3"
    >
      <div class="min-w-0">
        <strong>{{ trialBanner.title }}</strong>
        <span class="opacity-90"> — {{ trialBanner.body }}</span>
      </div>
    </div>

    <div
      v-if="banner"
      class="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-outline-gray-2 bg-surface-gray-1 px-3 py-2 text-sm"
    >
      <div class="min-w-0">
        <strong>{{ banner.title }}</strong>
        <span v-if="banner.body" class="text-ink-gray-6">
          — {{ banner.body }}</span
        >
      </div>
      <div class="flex gap-2">
        <Button
          size="sm"
          variant="subtle"
          label="View"
          @click="router.push(banner.link || '/whats-new')"
        />
        <Button
          size="sm"
          variant="ghost"
          label="Dismiss"
          @click="bannerDismissed = true"
        />
      </div>
    </div>
  </div>

  <Dialog
    v-model="termsOpen"
    :options="{ title: 'Terms & Conditions', size: '2xl' }"
  >
    <template #body-content>
      <div
        class="prose prose-sm max-h-[50vh] max-w-none overflow-y-auto"
        v-html="termsHtml"
      />
    </template>
    <template #actions>
      <Button
        variant="solid"
        label="I accept"
        :loading="accepting"
        @click="onAcceptTerms"
      />
    </template>
  </Dialog>

  <Dialog
    v-model="tourOpen"
    :options="{ title: 'Navigation tour', size: 'md' }"
  >
    <template #body-content>
      <div class="space-y-3">
        <div
          class="rounded-lg border border-outline-green-1 bg-surface-green-1 px-3 py-2 text-xs text-ink-green-3"
        >
          The matching item in the left sidebar is highlighted. Follow along or
          skip anytime.
        </div>
        <p class="text-sm font-semibold text-ink-gray-9">{{ step.title }}</p>
        <p class="text-sm text-ink-gray-6">{{ step.body }}</p>
        <p class="text-xs text-ink-gray-5">
          Step {{ Math.min(tourIdx + 1, tourSteps.length || 1) }} of
          {{ tourSteps.length || 1 }}
        </p>
        <Progress
          size="sm"
          :value="
            tourSteps.length > 0
              ? Math.round(((tourIdx + 1) / tourSteps.length) * 100)
              : 100
          "
        />
      </div>
    </template>
    <template #actions>
      <Button
        variant="ghost"
        label="Skip tour"
        :disabled="tourBusy"
        @click="tourSkip"
      />
      <Button
        variant="solid"
        :label="tourIdx >= (tourSteps.length || 1) - 1 ? 'Finish' : 'Next'"
        :loading="tourBusy"
        @click="tourNext"
      />
    </template>
  </Dialog>
</template>
